/* eslint-disable @typescript-eslint/indent */
/* eslint-disable no-debugger */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable no-undef */
import React from 'react';
import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';

import jsQR, { QRCode } from 'jsqr';

import { addPageAlert } from '../../../../PortalStore/Actions/PageAlertActions';

import './Html5QRScanner.scss';

interface IHtml5QRScanner {
    qrCodeSuccessCallback: (code: QRCode | null) => void;
}

interface ICamera {
    id: string;
    label: string;
}

export const Html5QRScanner: React.FC<IHtml5QRScanner> = ({ qrCodeSuccessCallback }) => {

    const [cameraError, setCameraError] = useState('');

    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    const frameRefTL = useRef<HTMLDivElement>(null);
    const frameRefTR = useRef<HTMLDivElement>(null);
    const frameRefBL = useRef<HTMLDivElement>(null);
    const frameRefBR = useRef<HTMLDivElement>(null);
    const frameRefLT = useRef<HTMLDivElement>(null);
    const frameRefLB = useRef<HTMLDivElement>(null);
    const frameRefRT = useRef<HTMLDivElement>(null);
    const frameRefRB = useRef<HTMLDivElement>(null);

    const dispatch = useDispatch();

    useEffect(() => {

        const getCameras = async () => {

            if (navigator.mediaDevices) {
                const response: any = await getCamerasFromMediaDevices();

                if (response.length == 0) {
                    setCameraError('No cameras detected');
                    dispatch(addPageAlert({
                        level: 'error',
                        title: 'Camera access error',
                        message: 'No cameras detected'
                    }));
                }
                return;
            }

            const isHttpsOrLocalhost = () => {
                if (location.protocol === 'https:') {
                    return true;
                }
                const host = location.host.split(':')[0];
                return host === '127.0.0.1' || host === 'localhost';
            };

            let errorMessage = 'Unable to query supported devices, unknown error.';
            if (!isHttpsOrLocalhost()) {
                errorMessage = 'Camera access is only supported in secure context like https or localhost.';
                dispatch(addPageAlert({
                    level: 'error',
                    title: 'Camera access error',
                    message: 'Camera access is only supported in secure context like https or localhost.'
                }));
            }

            setCameraError(errorMessage);
            return;
        };

        try {
            getCameras()
                .then(() => {
                    if (cameraError == '') {
                        start();
                    }
                });
        }
        catch (err) {
            setCameraError('Camera access error');
            dispatch(addPageAlert({
                level: 'error',
                title: 'Camera access error',
                message: err
            }));
        };
    }, []);

    const getCamerasFromMediaDevices = () => {
        return new Promise((resolve, reject) => {

            try {
                navigator.mediaDevices.getUserMedia({ audio: false, video: true })
                    .then((stream) => {
                        const closeActiveStreams = (stream) => {
                            const tracks = stream.getVideoTracks();
                            for (const track of tracks) {
                                track.enabled = false;
                                track.stop();
                                stream.removeTrack(track);
                            }
                        };
                        navigator.mediaDevices.enumerateDevices()
                            .then((devices) => {
                                const results: Array<ICamera> = [];
                                for (const device of devices) {
                                    if (device.kind === 'videoinput') {
                                        results.push({
                                            id: device.deviceId,
                                            label: device.label
                                        });
                                    }
                                }
                                closeActiveStreams(stream);
                                resolve(results);
                            })
                            .catch((err) => {
                                setCameraError(err.message);
                                dispatch(addPageAlert({
                                    level: 'error',
                                    title: 'Camera access error',
                                    message: err.message
                                }));
                                reject(`${err.name} : ${err.message}`);
                            });
                    })
                    .catch((err) => {
                        if (err.name == 'NotAllowedError') {
                            //console.log('Camera has been denied access');
                            setCameraError('Camera has been denied access');
                            dispatch(addPageAlert({
                                level: 'error',
                                title: 'Camera access required',
                                message: 'In order to continue, you will need to allow camera access in your browser settings.'
                            }));
                            reject(`${err.name} : ${err.message}`);
                        }
                        else {
                            setCameraError(err.message);
                            dispatch(addPageAlert({
                                level: 'error',
                                title: 'Camera access error',
                                message: err.message
                            }));
                            reject(`${err.name} : ${err.message}`);
                        }
                    });
            }
            catch (err) {
                setCameraError('Camera error');
                dispatch(addPageAlert({
                    level: 'error',
                    title: 'Camera error',
                    message: err
                }));
            }
        });
    };

    const start = async () => {
        //todo: make mandatory in tsx interface
        if (!qrCodeSuccessCallback
            || typeof qrCodeSuccessCallback != 'function') {
            throw 'qrCodeSuccessCallback is required and should be a function.';
        }

        navigator.mediaDevices
            .getUserMedia(
                {
                    video:
                    {
                        facingMode: 'environment'
                    }
                })
            .then(function (stream) {
                if (videoRef.current != null) {
                    videoRef.current!.srcObject = stream;
                    videoRef.current!.setAttribute('playsinline', 'true'); // required to tell iOS safari we don't want fullscreen
                    const playPromise = videoRef.current!.play();

                    if (playPromise !== undefined) {
                        playPromise
                            .then(() => {
                                requestAnimationFrame(tick);
                            });
                    }
                }

            });
    };

    function tick () {
        let ticker = true;
        if (videoRef.current && videoRef.current!.readyState === videoRef.current?.HAVE_ENOUGH_DATA) {

            const canvasElement = canvasRef.current?.getContext('2d');
            canvasRef.current!.hidden = false;

            videoRef.current!.style.height = (videoRef.current!.videoHeight * 0.5).toString() + 'px';;

            canvasRef.current!.height = videoRef.current?.videoHeight * 0.5;
            canvasRef.current!.width = videoRef.current?.videoWidth * 0.5;
            frameRef.current!.style.height = canvasRef.current?.height.toString() + 'px';
            frameRef.current!.style.width = canvasRef.current?.width.toString() + 'px';

            frameRefTL.current!.style.top = '-5px';
            frameRefTL.current!.style.left = '0px';

            frameRefTR.current!.style.top = '-5px';
            frameRefTR.current!.style.right = '0px';

            frameRefBL.current!.style.top = '180px';
            frameRefBL.current!.style.left = '0px';

            frameRefBR.current!.style.top = '180px';
            frameRefBR.current!.style.right = '0px';

            frameRefLT.current!.style.top = '-5px';
            frameRefLT.current!.style.left = '-5px';

            frameRefRT.current!.style.top = '140px';
            frameRefRT.current!.style.left = '-5px';

            frameRefLB.current!.style.top = '-5px';
            frameRefLB.current!.style.right = '-5px';

            frameRefRB.current!.style.top = '140px';
            frameRefRB.current!.style.right = '-5px';

            if (canvasElement && canvasRef.current) {
                canvasElement.drawImage(videoRef.current!, 0, 0, canvasRef.current.width, canvasRef.current.height);
                const imageData = canvasElement.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height);
                const code = jsQR(imageData.data, imageData.width, imageData.height, {
                    inversionAttempts: 'dontInvert',
                });
                if (code !== null && code!.data !== '') {
                    videoRef.current!.pause();
                    ticker = false;
                    qrCodeSuccessCallback(code);
                }
            }
        }
        if (ticker) {
            requestAnimationFrame(tick);
        }
    }

    return (
        <>

            <br />

            <div className='au-form-group qrcamera' style={{ display: cameraError === '' ? 'block' : 'none' }} >
                <video ref={videoRef} muted={true} playsInline={true}></video>

                <canvas ref={canvasRef} className='qrcanvas'></canvas>

                <div ref={frameRef} className='frame'>
                    <div ref={frameRefTL} className='frameHorizontal'></div>
                    <div ref={frameRefTR} className='frameHorizontal'></div>
                    <div ref={frameRefBL} className='frameHorizontal'></div>
                    <div ref={frameRefBR} className='frameHorizontal'></div>
                    <div ref={frameRefLT} className='frameVertical'></div>
                    <div ref={frameRefRT} className='frameVertical'></div>
                    <div ref={frameRefLB} className='frameVertical'></div>
                    <div ref={frameRefRB} className='frameVertical'></div>
                </div>
            </div>

            <br />

        </>

    );
};
